/**
 *  Deprecated.
 *  File to request data manually.
 * */
import humps from 'humps'

type JSONValue =
  | string
  | number
  | boolean
  | { [x: string]: JSONValue }
  | Array<JSONValue>

const csrf = () => {
  const node = document.querySelector('meta[name="csrf-token"]')
  if (node instanceof HTMLMetaElement) {
    return node.content
  }

  return ''
}

// New API routes will serve JSONAPI Content-Type
const getHeaders = (reqType: 'JSONAPI' | undefined): RequestInit => {
  const contentType =
    reqType === 'JSONAPI' ? 'application/vnd.api+json' : 'application/json'
  const profileUuid = localStorage.getItem('currentProfileUuid')

  return {
    credentials: 'include',
    headers: {
      'X-CSRF-Token': csrf(),
      'Content-Type': contentType,
      Accept: contentType,
      ...(profileUuid && { 'MC-Profile-Id': profileUuid }),
    },
  }
}

const formatBodyData = (data: object) =>
  JSON.stringify(humps.decamelizeKeys(data))

export const formatRequestData = (
  method: string,
  data: object | null,
  type: 'JSONAPI' | undefined,
) => {
  const requestData: RequestInit = {
    method,
    ...getHeaders(type),
  }
  if (data) {
    requestData.body = formatBodyData(data)
  }
  return requestData
}

const handleErrors = (response: Response): Promise<Response> =>
  new Promise((resolve, reject) => {
    if (!response) {
      reject(new Error('No response returned from fetch'))
      return
    }

    if (response.ok) {
      resolve(response)
      return
    }

    response
      .json()
      .then((json) => reject(json))
      .catch(() => {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({ message: response.statusText ?? 'Response not JSON' })
      })
  })

const getResponseBody = (
  response: Response,
): Promise<Record<string, JSONValue> | void> => {
  const bodyIsEmpty = response.status === 204
  if (bodyIsEmpty) {
    return Promise.resolve()
  }
  return response.json()
}

export const api = {
  performRequest<TResponse>(url: string, requestData: RequestInit = {}) {
    return new Promise<TResponse | undefined>((resolve, reject) => {
      fetch(url, requestData)
        .then(handleErrors)
        .then(getResponseBody)
        .then((response) =>
          resolve(
            response ? (humps.camelizeKeys(response) as TResponse) : undefined,
          ),
        )
        .catch((error: unknown) => reject(humps.camelizeKeys(error as object)))
    })
  },

  get<TResponse>(
    url: string,
    data: object | null = null,
    type: 'JSONAPI' | undefined = undefined,
  ) {
    const requestData = formatRequestData('GET', data, type)
    return api.performRequest<TResponse>(url, requestData)
  },

  post<TResponse>(
    url: string,
    data: object | null = null,
    type: 'JSONAPI' | undefined = undefined,
  ) {
    const requestData = formatRequestData('POST', data, type)
    return api.performRequest<TResponse>(url, requestData)
  },

  delete<TResponse>(
    url: string,
    data: object | null = null,
    type: 'JSONAPI' | undefined = undefined,
  ) {
    const requestData = formatRequestData('DELETE', data, type)
    return api.performRequest<TResponse>(url, requestData)
  },

  put<TResponse>(
    url: string,
    data: object | null = null,
    type: 'JSONAPI' | undefined = undefined,
  ) {
    const requestData = formatRequestData('PUT', data, type)
    return api.performRequest<TResponse>(url, requestData)
  },

  patch<TResponse>(
    url: string,
    data: object | null = null,
    type: 'JSONAPI' | undefined = undefined,
  ) {
    const requestData = formatRequestData('PATCH', data, type)
    return api.performRequest<TResponse>(url, requestData)
  },
}
